import { useMemo, useRef, useState } from 'react'
import {
  GridContainer,
  Button,
  TemplateBadges,
  MonochromeIcon,
  TemplateItem,
  TemplateDetailsBar,
  TemplateImagePreview,
  TemplateColorsPallette,
  TemplateColor,
  HeaderTitle,
  PickButtonContainer,
  TemplateNameLink
} from './styles'
import { i18n as I18n } from 'builder/utils/i18n'
import { trackInternalEvent, trackMarketingEvent } from '@rio/tracking'
import { withMediaQueries } from 'builder/components/MediaQueries'
import TemplatesCategories from './TemplatesCategories'
import Badges from 'builder/components/Badges'
import PropTypes from 'prop-types'
import { DocumentTypes } from 'builder/modules/constants'

const gridConfig = {
  v1: {
    mobile: 1,
    desktop: 3,
  },
  v2: {
    mobile: 2,
    desktop: 4,
  },
}

function Template({ template, onChoose, documentType }) {
  const { id, thumbnail, imagesWithColors, premium, supportedFormats, name } = template
  const imgRef = useRef()

  const setColor = (color, url) => {
    imgRef.current.src = url
  }

  const handleChooseClick = (id, name) => {
    onChoose(id)
    trackInternalEvent(`choose_sign_up_${documentType}_template`, {
      template: id,
      label: 'funnel',
    })
    trackMarketingEvent('Sign Up', name)
  }

  return (
    <TemplateItem>
      <TemplateImagePreview ref={imgRef} loading="lazy" alt={`${id} template`} src={thumbnail} />
      <PickButtonContainer onClick={() => handleChooseClick(id, name)}>
        <Button>{I18n.t('builder.sign_up.select')}</Button>
      </PickButtonContainer>
      <TemplateDetailsBar>
        <TemplateColorsPallette>
          {imagesWithColors?.length > 1 ? (
            imagesWithColors.map(({ color, url, default: isDefault }) => (
              <TemplateColor
                onMouseEnter={() => setColor(color, url)}
                key={color}
                isActive={false}
                color={color}
              />
            ))
          ) : (
            <MonochromeIcon />
          )}
        </TemplateColorsPallette>
        <TemplateBadges>
          {!premium && <Badges badges={['Free']} />}
          <Badges badges={supportedFormats} />
        </TemplateBadges>
      </TemplateDetailsBar>
      <TemplateNameLink onClick={() => handleChooseClick(id, name)}>{name}</TemplateNameLink>
    </TemplateItem>
  )
}

Template.propTypes = {
  template: PropTypes.object.isRequired,
  onChoose: PropTypes.func.isRequired,
  documentType: PropTypes.oneOf(Object.values(DocumentTypes)),
}

function TemplatesV2({ templates = [], onChoose, documentType, categories = [], variant = 'v1' }) {
  const [activeCategory, setActiveCategory] = useState({ slug: 'all' })

  const filteredTemplates = useMemo(() => {
    if (!activeCategory?.templates || activeCategory?.slug === 'all') return templates
    return templates.filter(template => activeCategory.templates.includes(template.id))
  }, [activeCategory, templates])

  const { mobile: mobileColumns, desktop: desktopColumns } = gridConfig[variant]

  return (
    <>
      <HeaderTitle>Select a template</HeaderTitle>
      <TemplatesCategories
        activeCategory={activeCategory}
        categories={categories}
        setActiveCategory={setActiveCategory}
        documentType={documentType}
      />
      <GridContainer mobileColumns={mobileColumns} desktopColumns={desktopColumns}>
        {filteredTemplates.map(template => (
          <Template
            key={template.id}
            template={template}
            onChoose={onChoose}
            documentType={documentType}
          />
        ))}
      </GridContainer>
    </>
  )
}

TemplatesV2.propTypes = {
  templates: PropTypes.array.isRequired,
  onChoose: PropTypes.func.isRequired,
  mediaQueries: PropTypes.object.isRequired,
  recommended: PropTypes.string,
  currentTemplate: PropTypes.string,
  documentType: PropTypes.oneOf(Object.values(DocumentTypes)),
  categories: PropTypes.array,
  variant: PropTypes.string,
}

export default withMediaQueries(TemplatesV2)
